import { useEffect, useState, useCallback, RefObject } from 'react';

interface UseVolumeControlOptions {
  videoRef: RefObject<HTMLVideoElement>;
}

interface UseVolumeControlReturn {
  // State
  volume: number;
  isMuted: boolean;
  // Actions
  handleVolumeUpdate: (newVolume: number) => void;
  handleMuteToggle: (muted: boolean) => void;
}

export function useVolumeControl(options: UseVolumeControlOptions): UseVolumeControlReturn {
  const { videoRef } = options;

  // State
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);

  // Handle volume update
  const handleVolumeUpdate = useCallback(
    (newVolume: number) => {
      if (videoRef.current) {
        videoRef.current.volume = newVolume;
        videoRef.current.muted = newVolume === 0;
      }
      setVolume(newVolume);
    },
    [videoRef],
  );

  // Handle mute toggle
  const handleMuteToggle = useCallback(
    (muted: boolean) => {
      if (videoRef.current) {
        videoRef.current.muted = muted;
      }
    },
    [videoRef],
  );

  // Sync volume state with video element
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const handleVolumeChange = () => {
      setVolume(video.volume);
      setIsMuted(video.muted || video.volume === 0);
    };

    video.addEventListener('volumechange', handleVolumeChange);

    return () => {
      video.removeEventListener('volumechange', handleVolumeChange);
    };
  }, [videoRef]);

  return {
    // State
    volume,
    isMuted,
    // Actions
    handleVolumeUpdate,
    handleMuteToggle,
  };
}
